import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useIsStandalone } from './useIsStandalone';

const STORAGE_KEY = 'hancomum-pwa-install-dismissed';
const TOAST_ID = 'pwa-install-guide';
// 마운트 직후 beforeinstallprompt 캡처를 기다렸다가 안내 (설치 버튼 vs 가이드 분기)
const SHOW_DELAY = 3000;

/**
 * PWA(앱)가 아닌 일반 브라우저로 접속했을 때 "앱으로 열기"를 부드럽게 권유한다.
 * - standalone(이미 앱)이면 표시하지 않는다.
 * - 한 번 닫으면 localStorage로 영구 dismiss.
 * - beforeinstallprompt가 잡히면 원클릭 설치, 아니면 설치 가이드(/install)로 유도.
 *   (HTTPS가 아닌 환경에서는 beforeinstallprompt가 발생하지 않아 가이드로 폴백된다)
 */
export function usePwaInstallGuide() {
  const isStandalone = useIsStandalone();
  const deferredPrompt = useRef<any>(null);
  const shown = useRef(false);

  useEffect(() => {
    if (isStandalone) return;
    if (localStorage.getItem(STORAGE_KEY) === '1') return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e;
    };
    const onInstalled = () => {
      localStorage.setItem(STORAGE_KEY, '1');
      toast.dismiss(TOAST_ID);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    const persistDismiss = () => localStorage.setItem(STORAGE_KEY, '1');

    const timer = setTimeout(() => {
      if (shown.current) return;
      shown.current = true;

      const dp = deferredPrompt.current;
      const action = dp
        ? {
            label: '설치',
            onClick: async () => {
              dp.prompt();
              try {
                const res = await dp.userChoice;
                if (res.outcome === 'accepted') localStorage.setItem(STORAGE_KEY, '1');
              } catch {
                /* noop */
              }
              deferredPrompt.current = null;
            },
          }
        : {
            label: '설치 방법 보기',
            onClick: () => window.location.assign('/install'),
          };

      toast.info('앱으로 설치하면 더 편해요', {
        id: TOAST_ID,
        description: 'UBIFLOW를 앱으로 설치하면 더 빠르게 실행하고 알림도 받을 수 있어요.',
        duration: Infinity,
        action,
        onDismiss: persistDismiss,
      });
    }, SHOW_DELAY);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [isStandalone]);
}
